import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { betStore } from '@pascal/pascal-core/shared/core/store';
import {
  FreeBetWinningType,
  getAmountWithCurrency,
  minus,
  multiply,
  parseNumberByPrecision,
  toFixed,
  UpdatedFreeBetDataItemKeys,
} from '@pascal/pascal-core';
import { parseOddByPrecision } from '../../../helpers';
import { TranslationKeys } from '../../../enums/translationKeys';
import { doBetStore } from '../../../store/doBetStore';

const CashOutBlock = observer(
  ({
    t,
    index,
    userBet,
    cashOut,
    demoMode,
    precision,
    reportList,
    wageringOdd,
    freeBetsList,
    activeFreeBet,
    currencySymbolOrCode,
    disabledCashOutButton,
  }) => {
    const [showPulsing, setShowPulsing] = useState(false);
    const { maxPayoutData } = doBetStore;
    const odd = useMemo(() => {
      const filteredList = reportList.slice(
        0,
        betStore.repliedBetData
          ? betStore.repliedBetData?.roundData.length
          : reportList?.length - userBet.betData.betStep
      );
      const listLength = filteredList.length;

      if (listLength) {
        const reversedList = filteredList.slice().reverse();
        const totalOdd = reversedList.reduce(
          (accumulator, current) =>
            parseNumberByPrecision(accumulator, current?.odd || 1, precision, true),
          1
        );
        return parseOddByPrecision(totalOdd, precision);
      }

      return 0;
    }, [reportList]);
    const freeBetMinOdd = useMemo(() => {
      const notActiveFreeBetMinOdd = freeBetsList?.find((item) => item?.id === userBet?.freeBetId);
      return activeFreeBet?.id
        ? activeFreeBet?.minOdd
        : userBet?.freeBetId
        ? notActiveFreeBetMinOdd?.minOdd
        : '';
    }, [freeBetsList, activeFreeBet, userBet?.freeBetId]);

    const freeBetAmount =
      maxPayoutData?.[index]?.isBetAccepted &&
      maxPayoutData?.[index]?.freeBetId &&
      +maxPayoutData?.[index]?.freeBetType === FreeBetWinningType.Win
        ? toFixed(
            minus(
              Number(toFixed(multiply(odd || 1, Number(userBet?.amount)), 2)),
              Number(userBet?.amount) || 0
            ) || 0,
            2
          )
        : toFixed(multiply(odd || 1, Number(userBet?.amount || 0)) || 0, 2);

    const userBetCount = useMemo(() => {
      return reportList.filter(
        (result, index) => index < reportList.length - userBet.betData.betStep
      ).length;
    }, [reportList]);

    useEffect(() => {
      setShowPulsing(true);
      const id = setTimeout(() => setShowPulsing(false), 500);
      return () => {
        clearTimeout(id);
        setShowPulsing(false);
      };
    }, [odd, reportList.length]);

    useEffect(() => {
      if (
        maxPayoutData[index]?.maxPayout &&
        Number(freeBetAmount) >= Number(maxPayoutData[index]?.maxPayout)
      ) {
        doBetStore?.updateMaxPayoutBetData({
          sectionId: Number(index),
          updatedDataByKeys: {
            [UpdatedFreeBetDataItemKeys.IS_SHOW_POP_UP]: true,
            [UpdatedFreeBetDataItemKeys.IS_ALREADY_SEEN_POP_UP]:
              maxPayoutData[index]?.isAlreadySeenPopUp,
            [UpdatedFreeBetDataItemKeys.CASH_OUT_AMOUNT]: freeBetAmount,
          },
        });
      }
    }, [odd, userBet?.amount, freeBetAmount]);

    return (
      <div className='fs-bet-block'>
        <span className='fs-bet-block-label'>
          <i className='total-odd-text'>{t(TranslationKeys.TOTAL_ODD)}: </i>
          <span className={showPulsing ? 'pulsing' : ''}>
            x{userBetCount ? odd : <span className='fs-total-add_span'> - - </span>}
          </span>
        </span>
        <div className='fs-bet-num'>
          {getAmountWithCurrency({ isReturnPreValue: true }) ? (
            <>
              <span className='fs-bet-num-currency'> {currencySymbolOrCode}</span>
              {+freeBetAmount > +maxPayoutData[index]?.maxPayout
                ? maxPayoutData[index]?.maxPayout
                : freeBetAmount}
            </>
          ) : (
            <>
              {+freeBetAmount > +maxPayoutData[index]?.maxPayout
                ? maxPayoutData[index]?.maxPayout
                : freeBetAmount}
              <span className='fs-bet-num-currency'> {currencySymbolOrCode}</span>
            </>
          )}
        </div>
        <button
          type='button'
          className={`fs-action-panel-button ${
            freeBetMinOdd || wageringOdd
              ? `free-bet ${(freeBetMinOdd || wageringOdd) > odd ? 'free-bet-disabled' : ''}`
              : ''
          }`}
          data-type='cash-out'
          onClick={() => cashOut(index)}
          disabled={disabledCashOutButton[index] || (freeBetMinOdd || wageringOdd) > odd}
        >
          <p className='fs-action-panel-button-txt'>{t(TranslationKeys.CASHOUT)}</p>
          <p
            className={`btn-game-small-txt ${
              (freeBetMinOdd || wageringOdd) < odd && disabledCashOutButton[index] ? 'disabled' : ''
            }`}
          >
            {(freeBetMinOdd || wageringOdd) && !demoMode
              ? `${t(TranslationKeys.MIN_ODD)} x${freeBetMinOdd || wageringOdd}`
              : ''}
          </p>
        </button>
      </div>
    );
  }
);
export default CashOutBlock;
